import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  Header,
  Pagination,
  Table,
} from '@cloudscape-design/components';
import { formatDate } from '@parallel-fluidics/shared/shipping';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AddPOModal from 'pages/QboQuotes/addPOModal';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotifications } from '../../features/notifications';
import { formatCurrency } from '../../utils';

const StatusColorMap = Object.freeze({
  Converted: 'blue',
  Accepted: 'green',
  Pending: 'severity-neutral',
  Closed: 'severity-high',
  Rejected: 'severity-critical',
});

const pageSize = 20;
export default function QboQuotes() {
  const functions = getFunctions();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [downloadingQuotes, setDownloadingQuotes] = useState(new Set());
  const [quoteToCreate, setQuoteToCreate] = useState(null);

  const {
    data: { pages = [], pageParams = [] } = {},
    hasNextPage,
    isFetching,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['getQBOEstimates'],
    queryFn: async ({ pageParam }) => {
      const listQBOEstimates = httpsCallable(functions, 'listQBOEstimatesV2');
      const { data } = await listQBOEstimates({
        startingAfter: pageParam,
        pageSize,
      });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (
      lastPage?.maxResults === pageSize
        ? lastPage.maxResults + lastPage.startPosition
        : undefined
    ),
    refetchOnWindowFocus: false,
  });

  const handleDownloadPDF = useCallback(async (estimateId, docNumber) => {
    try {
      setDownloadingQuotes((prev) => new Set(prev).add(estimateId));
      const getQuotePDF = httpsCallable(functions, 'getQBOEstimatePDFV2');
      const response = await getQuotePDF({ estimateId });
      // Decode base64 PDF
      const { pdfBase64 } = response.data;
      const blob = new Blob([Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0))], {
        type: 'application/pdf',
      });
      // Create a downloadable link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Estimate-${docNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      addNotification({
        type: 'error',
        dismissible: true,
        content: `Error downloading PDF: ${error.message}`,
      });
    } finally {
      setDownloadingQuotes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(estimateId);
        return newSet;
      });
    }
  }, [addNotification, functions]);

  const quotesList = useMemo(() => (pages[currentPageIndex - 1]?.Estimate || [])
    .map((estimate) => {
      let qboEstimates = null;
      let orderId = null;
      estimate.CustomField.forEach((field) => {
        if (field.StringValue) {
          if (field.Name === 'qboEstimates') {
            qboEstimates = field.StringValue;
          } else if (field.Name === 'orders') {
            orderId = field.StringValue;
          }
        }
      });
      return {
        ...estimate,
        amount: (
          <div style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
            <Box color={estimate.TxnStatus === 'Closed' ? 'text-status-inactive' : ''}>
              {formatCurrency(estimate.TotalAmt)}
            </Box>
            <Box color="text-status-inactive">{estimate.CurrencyRef?.value}</Box>
          </div>
        ),
        statusBadge: (
          <Badge color={StatusColorMap[estimate.TxnStatus]}>
            {estimate.TxnStatus}
          </Badge>
        ),
        createdOn: formatDate(estimate.MetaData.CreateTime),
        updatedOn: formatDate(estimate.MetaData.LastUpdatedTime),
        actions: (
          <ButtonDropdown
            items={[
              { id: 'download', text: 'Download Estimate PDF' },
              (orderId
                ? { id: 'editOrder', text: 'Edit Order' }
                : { id: 'createOrder', text: 'Create an Order', disabled: estimate.TxnStatus !== 'Pending' || !qboEstimates }),
            ]}
            onItemClick={({ detail }) => {
              switch (detail.id) {
                case 'download':
                  handleDownloadPDF(estimate.Id, estimate.DocNumber);
                  break;
                case 'createOrder':
                  setQuoteToCreate(estimate);
                  break;
                case 'editOrder':
                  navigate(`/orders/update/${orderId.trim()}`);
                  break;
                default:
              }
            }}
            expandToViewport
            loading={downloadingQuotes.has(estimate.Id)}
            variant="icon"
          />
        ),
      };
    }), [currentPageIndex, downloadingQuotes, handleDownloadPDF, navigate, pages]);

  return (
    <>
      <Table
        header={(
          <Header
            variant="awsui-h1-sticky"
            actions={(
              <Button onClick={() => navigate('/qbo/create/estimate')}>
                Create Estimate (Manual)
              </Button>
            )}
          >
            QBO Estimates
          </Header>
      )}
        empty={(
          <Box
            color="inherit"
            fontWeight="bold"
          >
            No estimate found
          </Box>
      )}
        variant="full-page"
        stickyHeader
        loading={isFetching}
        items={quotesList}
        resizableColumns
        pagination={(
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => {
              // only fetchNextPage for new page
              if (detail.currentPageIndex > pageParams.length) {
                fetchNextPage();
              }
              setCurrentPageIndex(detail.currentPageIndex);
            }}
            openEnd={hasNextPage}
            pagesCount={pageParams.length}
          />
      )}
        columnDefinitions={[
          {
            header: 'Amount',
            cell: (item) => item.amount,
            width: 140,
          },
          {
            header: 'Status',
            cell: (item) => item.statusBadge,
          },
          {
            header: 'Estimate Number',
            cell: (item) => item.DocNumber,
          },
          {
            header: 'Customer',
            cell: (item) => item.CustomerRef?.name,
          },
          {
            header: 'Created',
            cell: (item) => item.createdOn,
          },
          {
            header: 'Updated',
            cell: (item) => item.updatedOn,
          },
          {
            header: 'Action',
            cell: (item) => item.actions,
          },
        ]}
      />
      {quoteToCreate && (
        <AddPOModal
          quote={quoteToCreate}
          open={Boolean(quoteToCreate)}
          onClose={() => setQuoteToCreate(null)}
        />
      )}
    </>
  );
}
